import { Relationship, relationships, UnitedStateCode } from "@goodfill/domain-customer-application-types";
import { Flex, Grid, InputSelect, InputSelectOption, InputText, Span } from "@goodfill/ui-components";
import { InfoTooltip } from "../../../../../../ui-components/tooltip/InfoTooltip";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { PhoneInput } from "../../../../../phoneInput/PhoneInput";
import { FormYesOrNoQuestion } from "../../FormYesOrNoQuestion";
import { StockSymbolsField } from "../../StockSymbolsField";
import { RecognizedEmployerSelect } from "../../RecognizedEmployerSelect";
import { Controller, useFormContext } from "react-hook-form";
import { capitalizeEveryFirstWordLetter } from "@goodfill/string";
import {
  BrokerCompany,
  CustomerApplicationFormState,
  CustomerApplicationFormStateBroker,
  defaultBroker,
  selectedRecognizedEmployerTransform,
} from "../../../formState/formState";
import { StateInput } from "../../../../../../countries/StateInput";
import { defaultAddressWithoutCountryScheme } from "../../../../../../form/zod/formAddress";
import { deserializePhone, serializePhone } from "../../../../../../form/zod/formPhone";

const brokerAffiliationRelationshipOptions: InputSelectOption[] = relationships.map((value) => ({
  value,
  label: capitalizeEveryFirstWordLetter(value.replace("-", " ")),
}));

export const Step7Regulatory = ({ areAllDisabled }: { areAllDisabled: boolean }) => {
  const { control, setValue, watch, trigger } = useFormContext<CustomerApplicationFormState>();

  const watchIsBroker = watch("step7.isBroker");
  const watchSelectedRecognizedEmployer = watch("step7.broker.selectedRecognizedEmployer");
  const watchBrokerAffiliationRelationship = watch("step7.broker.brokerAffiliationRelationship");
  const watchIsEmployeeOfPubliclyTradedCompany = watch("step7.isEmployeeOfPubliclyTradedCompany");
  const watchHasControlOfPubliclyTradedCompany = watch("step7.hasControlOfPubliclyTradedCompany");

  // If `employerId` part in the `selectedRecognizedEmployer` is empty,
  // it means the employer was added manually and not selected from the list
  const showManuallyAddedBrokerEmployer =
    watchSelectedRecognizedEmployer !== undefined &&
    !selectedRecognizedEmployerTransform.toObject(watchSelectedRecognizedEmployer)?.employerId?.trim();

  return (
    <Flex direction="col" horizontalPadding={8} verticalPadding={8} borderBottom="thin" items="center">
      <Span size="2xl" uppercase={false} font="semibold">
        Regulatory information
      </Span>

      <Span className="Steps_welcomeMessage" wrap={true}>
        To open a free account and start you investing safely, we need to know more about you. Let's get started!
      </Span>

      <Flex className="w-full max-w-2xl">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id="isBroker"
              label="Is the applicant or any immediate family member who resides in the same household, registered as a broker-dealer or an employee, director or owner of a securities or commodities brokerage firm?"
              value={field.value}
              onChange={(value) => {
                // Necessary cast to hack react-hook-from absence of support for the "non-valid" state
                setValue("step7.broker", value ? (defaultBroker as CustomerApplicationFormStateBroker) : null);

                field.onChange(value);
              }}
              validation={error?.message ? [error.message] : []}
              fill
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={areAllDisabled}
              className="w-full-important"
            />
          )}
          name="step7.isBroker"
          control={control}
        />
      </Flex>

      {watchIsBroker ? (
        <>
          <Flex className="w-full max-w-2xl">
            <Controller
              render={({ field, fieldState: { error } }) => (
                <FormYesOrNoQuestion
                  id="duplicateStatementRequired"
                  label={
                    <>
                      Is Duplicate statement required?
                      <InfoTooltip text="You should check with the broker/dealer with whom you are affiliated however in nearly all cases, duplicate statements must be sent to them." />
                    </>
                  }
                  value={field.value}
                  onChange={(a) => {
                    field.onChange(a);
                  }}
                  validation={error?.message ? [error.message] : []}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  disabled={areAllDisabled}
                />
              )}
              name="step7.broker.duplicateStatementRequired"
              control={control}
            />
          </Flex>

          <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label="Affiliation Relationship"
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputSelect
                    placeholder="Select Affiliation Relationship"
                    options={brokerAffiliationRelationshipOptions}
                    value={field.value ?? ""}
                    selected={field.value}
                    onChange={(value) => {
                      field.onChange(value as Relationship | null);
                      // When relationship !== self, the name is required so trigger validation
                      trigger("step7.broker.brokerName");
                    }}
                    border={error === undefined ? "neutral" : "red"}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id="brokerAffiliationRelationship"
                    required
                    disabled={areAllDisabled}
                  />
                  {/* </InputSelect> */}
                </FieldGroup>
              )}
              name="step7.broker.brokerAffiliationRelationship"
              control={control}
            />

            {watchBrokerAffiliationRelationship && watchBrokerAffiliationRelationship !== "self" ? (
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <FieldGroup
                    label="Name of the affiliated person"
                    validation={error?.message ? [error.message] : []}
                    className="w-80-sm w-full-after-md"
                    fill
                  >
                    <InputText
                      value={field.value ?? ""}
                      setValue={field.onChange}
                      border={error === undefined ? "neutral" : "red"}
                      placeholder="Enter Name"
                      onBlur={field.onBlur}
                      ref={field.ref}
                      id="brokerName"
                      required
                      disabled={areAllDisabled}
                    />
                  </FieldGroup>
                )}
                name="step7.broker.brokerName"
                control={control}
              />
            ) : null}
          </Grid>

          <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
            <Controller
              render={({ field, fieldState: { error } }) => {
                const fieldValue = selectedRecognizedEmployerTransform.toObject(field.value);

                return (
                  <FieldGroup
                    label={"Employer"}
                    validation={error?.message ? [error.message] : []}
                    className="w-80-sm w-full-after-md"
                    fill
                  >
                    <RecognizedEmployerSelect
                      selectedRecognizedEmployer={fieldValue}
                      onItemSelect={(recognizedEmployer) => {
                        field.onChange(selectedRecognizedEmployerTransform.toState(recognizedEmployer));

                        if (!recognizedEmployer.employerId) {
                          // Necessary cast to hack react-hook-from absence of support for the "non-valid" state
                          setValue("step7.broker.brokerCompany", {
                            phone: "",
                            email: "",
                            name: recognizedEmployer.employerName,
                            address: defaultAddressWithoutCountryScheme,
                          } as BrokerCompany);
                        } else {
                          setValue("step7.broker.brokerCompany", null);
                        }
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      disabled={areAllDisabled}
                    />
                  </FieldGroup>
                );
              }}
              name="step7.broker.selectedRecognizedEmployer"
              control={control}
            />

            {showManuallyAddedBrokerEmployer ? (
              <>
                <Controller
                  render={({ field, fieldState: { error } }) => {
                    const [countryCallCode, nationalNumber] = deserializePhone(field.value);

                    return (
                      <FieldGroup
                        label="Company phone"
                        validation={error?.message ? [error.message] : []}
                        className="w-80-sm w-full-after-md"
                        fill
                      >
                        <PhoneInput
                          value={{ countryCallCode, nationalNumber }}
                          onChange={(value) =>
                            field.onChange(serializePhone(value.countryCallCode, value.nationalNumber))
                          }
                          isValid={error === undefined}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          disabled={areAllDisabled}
                        />
                      </FieldGroup>
                    );
                  }}
                  name="step7.broker.brokerCompany.phone"
                  control={control}
                />

                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FieldGroup
                      label="Company Email"
                      validation={error?.message ? [error.message] : []}
                      className="w-80-sm w-full-after-md"
                      fill
                    >
                      <InputText
                        value={field.value ?? ""}
                        setValue={field.onChange}
                        border={error === undefined ? "neutral" : "red"}
                        placeholder="Enter email"
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={areAllDisabled}
                      />
                    </FieldGroup>
                  )}
                  name="step7.broker.brokerCompany.email"
                  control={control}
                />

                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FieldGroup
                      label={"Address"}
                      validation={error?.message ? [error.message] : []}
                      className="w-80-sm w-full-after-md"
                      fill
                    >
                      <InputText
                        value={field.value ?? ""}
                        setValue={field.onChange}
                        border={error === undefined ? "neutral" : "red"}
                        placeholder="Street 1"
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={areAllDisabled}
                      />
                    </FieldGroup>
                  )}
                  name={"step7.broker.brokerCompany.address.street1"}
                  control={control}
                />

                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FieldGroup
                      label={"Address 2"}
                      validation={error?.message ? [error.message] : []}
                      className="w-80-sm w-full-after-md"
                      fill
                    >
                      <InputText
                        value={field.value ?? ""}
                        setValue={field.onChange}
                        border={error === undefined ? "neutral" : "red"}
                        placeholder="Street 2"
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={areAllDisabled}
                      />
                    </FieldGroup>
                  )}
                  name={"step7.broker.brokerCompany.address.street2"}
                  control={control}
                />

                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FieldGroup
                      label={"City"}
                      validation={error?.message ? [error.message] : []}
                      className="w-80-sm w-full-after-md"
                      fill
                    >
                      <InputText
                        value={field.value ?? ""}
                        setValue={field.onChange}
                        border={error === undefined ? "neutral" : "red"}
                        placeholder="City"
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={areAllDisabled}
                      />
                    </FieldGroup>
                  )}
                  name={"step7.broker.brokerCompany.address.city"}
                  control={control}
                />

                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FieldGroup
                      label={"State"}
                      validation={error?.message ? [error.message] : []}
                      className="w-80-sm w-full-after-md"
                      fill
                    >
                      <StateInput
                        selected={field.value as UnitedStateCode}
                        onChange={field.onChange}
                        isValid={error === undefined}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={areAllDisabled}
                      />
                    </FieldGroup>
                  )}
                  name={"step7.broker.brokerCompany.address.state"}
                  control={control}
                />

                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <FieldGroup
                      label={"Zip Code"}
                      validation={error?.message ? [error.message] : []}
                      className="w-80-sm w-full-after-md"
                      fill
                    >
                      <InputText
                        value={field.value ?? ""}
                        setValue={field.onChange}
                        border={error === undefined ? "neutral" : "red"}
                        placeholder="Zip"
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={areAllDisabled}
                      />
                    </FieldGroup>
                  )}
                  name={"step7.broker.brokerCompany.address.zipCode"}
                  control={control}
                />
              </>
            ) : null}
          </Grid>
        </>
      ) : null}

      <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id="isEmployeeOfPubliclyTradedCompany"
              label={
                <>
                  Employee of Publicly Traded Company
                  <InfoTooltip text="If you are an employee of one or more publicly traded companies, list the company stock symbols. " />
                </>
              }
              value={field.value}
              onChange={(value) => {
                field.onChange(value);

                if (!value) {
                  setValue("step7.employeeOfPubliclyTradedCompanyStockSymbols", []);
                }
              }}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              ref={field.ref}
              className="w-80-sm w-full-after-md"
            />
          )}
          name="step7.isEmployeeOfPubliclyTradedCompany"
          control={control}
        />

        {watchIsEmployeeOfPubliclyTradedCompany ? (
          <Controller
            render={({ field, fieldState: { error } }) => (
              <StockSymbolsField
                label={"Company stock symbols"}
                symbols={field.value ?? []}
                onChangeState={field.onChange}
                validation={error?.message ? [error.message] : []}
                onBlur={field.onBlur}
                ref={field.ref}
                disabled={areAllDisabled}
              />
            )}
            name="step7.employeeOfPubliclyTradedCompanyStockSymbols"
            control={control}
          />
        ) : null}
      </Grid>

      <Grid cols={2} gap={0.75} className="w-full max-w-2xl">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id="hasControlOfPubliclyTradedCompany"
              label={
                <>
                  Control of Publicly Traded Company
                  <InfoTooltip text="If you are a control person as defined in Rule 405 of the Securities Exchange Act, list the applicable company stock symbols." />
                </>
              }
              value={field.value}
              onChange={(value) => {
                field.onChange(value);

                if (!value) {
                  setValue("step7.controlOfPubliclyTradedCompanyStockSymbols", []);
                }
              }}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={areAllDisabled}
              className="w-80-sm w-full-after-md"
            />
          )}
          name="step7.hasControlOfPubliclyTradedCompany"
          control={control}
        />

        {watchHasControlOfPubliclyTradedCompany ? (
          <Controller
            render={({ field, fieldState: { error } }) => (
              <StockSymbolsField
                label={"Company stock symbols"}
                symbols={field.value ?? []}
                onChangeState={field.onChange}
                validation={error?.message ? [error.message] : []}
                onBlur={field.onBlur}
                ref={field.ref}
                disabled={areAllDisabled}
              />
            )}
            name="step7.controlOfPubliclyTradedCompanyStockSymbols"
            control={control}
          />
        ) : null}
      </Grid>
    </Flex>
  );
};
